import { BulbOutlined } from '@ant-design/icons';
import { Button, Form, Modal } from 'antd';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { FeaturesComponent } from 'app/components/commons/Features/Features';
import { BasicInfo } from 'app/components/commons/Room/BasicInfo/BasicInfo';
import { PicturesComponent } from 'app/components/commons/Room/Pictures/Pictures';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useRoomFeatures } from 'app/hooks/data/rooms/useGetRoomFeatures';
import type { Room } from 'app/typings';
import type { Bed } from 'app/typings/rooms';
import { getBlockedBathroomFeatures } from 'app/utils/room';

import type { RoomFeature } from '../../../Types';

import { BedsBuilder } from './BedBuilder/BedsBuilder';
import './RoomModal.scss';

export const RoomModal: FC<Props> = ({
  saveRoom,
  close,
  room,
  categories,
  hotelId,
}) => {
  const [form] = Form.useForm();
  const { data: roomFeatures = [] } = useRoomFeatures();

  const basicFeatureIds = useMemo(
    () =>
      roomFeatures
        .filter((feature) => feature.basic)
        .map((feature) => feature.id),
    [roomFeatures]
  );

  const hasSeededFeatureIds = useRef(false);

  useEffect(() => {
    if (hasSeededFeatureIds.current || basicFeatureIds.length === 0) {
      return;
    }

    hasSeededFeatureIds.current = true;

    if ((room as Partial<Room>).featureIds === undefined) {
      form.setFieldsValue({ featureIds: basicFeatureIds });
    }
  }, [basicFeatureIds, room, form]);

  const featuresByCategory = useMemo(() => {
    return roomFeatures.reduce(
      (acc: Record<string, RoomFeature[]>, feature) => {
        if (!acc[feature.category]) {
          acc[feature.category] = [];
        }

        acc[feature.category].push(feature);

        return acc;
      },
      {}
    );
  }, [roomFeatures]);

  const validate = ({
    area,
    beds,
    categoryId,
    categoryRoomCount,
    pictures,
  }: Partial<Room>) => {
    if (!Number.isInteger(area)) {
      return false;
    }

    if (!beds) {
      return false;
    }

    if (beds.some((bed: Bed) => !Number.isInteger(bed.categoryId))) {
      return false;
    }

    if (!Number.isInteger(categoryId)) {
      return false;
    }

    if (!Number.isInteger(categoryRoomCount)) {
      return false;
    }

    if (!pictures?.length) {
      return false;
    }

    return true;
  };

  const renderFooter = (values: Partial<Room>) => {
    const isValid = validate(values);

    return (
      <>
        <Button key="cancel" onClick={() => close()}>
          Cancel
        </Button>
        <Button
          key="submit"
          onClick={() => form.submit()}
          disabled={!isValid}
          type="primary"
        >
          Save
        </Button>
      </>
    );
  };

  const onFinish = async (values: Partial<Room>) => {
    const formatted = {
      ...room,
      ...values,
      beds: values.beds?.map((bed: Bed) => ({
        ...bed,
        included: bed.included !== false,
        price: bed.price || 0,
      })),
      hasPRM: values.hasPRM || false,
      hotelId,
    };

    saveRoom(formatted as Room);
    close();
  };

  const initialValues = {
    ...room,
    // @ts-ignore -- legacy directive, needs investigation
    beds: room.beds?.map((bed) => ({
      ...bed,
      included: bed.included,
    })) || [{}],
    // @ts-ignore -- legacy directive, needs investigation
    featureIds: room.featureIds || basicFeatureIds,
    // @ts-ignore -- legacy directive, needs investigation
    hasPRM: room.hasPRM,
    // @ts-ignore -- legacy directive, needs investigation
    pictures: room.pictures || [],
  };

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
    >
      {(values) => (
        <Modal
          title="Edit room category"
          width={800}
          className="room-modal-builder"
          wrapClassName="hotel-builder-modal"
          onCancel={() => close()}
          footer={renderFooter(values)}
          open
          centered
        >
          <div>
            <div className="title">Basic information</div>
            <div className="block">
              <BasicInfo values={values as Room} />
            </div>
          </div>
          <div>
            <div className="title">Beds</div>
            <div className="block">
              <Form.List name="beds">
                {(fields, { add: addBed, remove: removeBed }) => (
                  <BedsBuilder
                    fields={fields}
                    add={addBed}
                    remove={removeBed}
                    values={values as Room}
                  />
                )}
              </Form.List>
            </div>
          </div>
          <div>
            <div className="title">Features</div>
            <div className="block">
              <Form.Item name="featureIds">
                <FeaturesComponent
                  featuresByCategory={featuresByCategory}
                  getDisabledFeatureIds={getBlockedBathroomFeatures}
                />
              </Form.Item>
            </div>
          </div>
          <div>
            <div className="title">Photos</div>
            <div className="block">
              <Form.Item name="pictures">
                <PicturesComponent
                  roomCategoryId={(room as Room)?.categoryId}
                  categories={categories}
                  hotelId={hotelId}
                  previewable
                  removable
                  picturesCategories={[
                    { name: 'Media gallery', type: 'official' },
                  ]}
                  kind="official"
                />
              </Form.Item>

              <div className="cool-to-know">
                <div className="border" />
                <div className="content">
                  <div className="title">
                    <BulbOutlined />
                    <div>Cool to know</div>
                  </div>
                  <div className="text">
                    Guests are more likely to book your package if there are at
                    least 5 pictures of your room and bathroom.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};

type Props = {
  saveRoom: (values: Room) => void;
  close: () => void;
  room: Room | {};
  categories: { [id: number]: string };
  hotelId: number;
};
